import { FunctionsHttpError, Session, User } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import { supabase, SUPABASE_ANON_KEY, SUPABASE_URL } from '../supabase';
import { clearComensalCiudades, setComensalCiudadPreferida } from './comensal-ciudad';
import { normalizePhoneE164 } from '../utils/normalizePhoneE164';
import { REGISTRO_EMAIL_EN_USO } from '../utils/registroErrors';

export type UserRole = 'owner' | 'master' | 'admin' | 'user';
export type UserStatus = 'active' | 'inactive' | 'suspended';
export type UserTalla = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';

export interface UserProfile {
  id: string;
  email: string;
  nombre?: string;
  apellido?: string;
  telefono?: string;
  cedula?: string;
  cargo?: string;
  instagram?: string;
  fecha_nacimiento?: string;
  sexo?: 'masculino' | 'femenino' | null;
  /** Estado del país (p. ej. Miranda), rellenado en el perfil del comensal. */
  estado?: string | null;
  ciudad_otra?: string | null;
  talla?: string;
  role: UserRole;
  status: UserStatus;
  /** Columna legacy / OAuth; preferir `foto_url` si existe. */
  avatar_url?: string;
  /** URL de foto de perfil en `user_profiles` (fuente principal en producción). */
  foto_url?: string;
  creado_por?: string;
  fecha_creacion: string;
  fecha_actualizacion: string;
  ultimo_acceso?: string;
  ciudades_ids?: string[];
  ciudades_nombres?: string[];
}

export interface Permiso {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  categoria: string;
  nivel: 'view' | 'create' | 'edit' | 'delete' | 'manage';
}

export interface UsuarioPermiso {
  id: number;
  usuario_id: string;
  permiso_id: number;
  permiso?: Permiso;
  otorgado_por?: string;
  fecha_otorgado: string;
}

/** URL de foto de perfil: `foto_url` (producción) o `avatar_url` (legacy/OAuth). */
export function getUserProfilePhotoUrl(
  profile: Pick<UserProfile, 'foto_url' | 'avatar_url'> | null | undefined
): string | null {
  const foto = (profile?.foto_url ?? '').trim();
  if (foto) return foto;
  const avatar = (profile?.avatar_url ?? '').trim();
  return avatar || null;
}

function withNormalizedPhotoFields<T extends Record<string, unknown>>(user: T): T & {
  foto_url?: string;
  avatar_url?: string;
} {
  const foto = String((user as { foto_url?: unknown }).foto_url ?? '').trim();
  const avatar = String((user as { avatar_url?: unknown }).avatar_url ?? '').trim();
  const photo = foto || avatar || undefined;
  return {
    ...user,
    ...(photo ? { foto_url: photo, avatar_url: photo } : {}),
  };
}

type CiudadesUsuario = { ids: string[]; nombres: string[] };

async function restGetJson(pathAndQuery: string, accessToken: string, signal?: AbortSignal) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${pathAndQuery}`, {
    method: 'GET',
    signal,
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`REST ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json();
}

async function fetchCiudadesWithToken(
  userId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<CiudadesUsuario> {
  const empty: CiudadesUsuario = { ids: [], nombres: [] };
  try {
    const relaciones = (await restGetJson(
      `usuarios_ciudades?usuario_id=eq.${encodeURIComponent(userId)}&select=usuario_id,ciudad_id`,
      accessToken,
      signal
    )) as { usuario_id: string; ciudad_id: number }[];
    if (!Array.isArray(relaciones) || relaciones.length === 0) return empty;
    const ids = [...new Set(relaciones.map((r) => r.ciudad_id).filter((id) => id != null))];
    const idList = ids.join(',');
    const ciudades = idList
      ? ((await restGetJson(
          `ciudades?id=in.(${idList})&select=id,nombre`,
          accessToken,
          signal
        )) as { id: number; nombre: string }[])
      : [];
    const nombresById = new Map((ciudades ?? []).map((c) => [c.id, c.nombre]));
    return {
      ids: ids.map(String),
      nombres: ids.map((id) => nombresById.get(id)).filter((n): n is string => Boolean(n)),
    };
  } catch {
    return empty;
  }
}

/** PostgREST GET + `.in()` se rompe con lista vacía o URL demasiado larga. */
const USUARIOS_CIUDADES_IN_CHUNK = 80;

async function fetchCiudadesMapForUsers(userIds: string[]): Promise<Map<string, CiudadesUsuario>> {
  const map = new Map<string, CiudadesUsuario>();
  const uniqueIds = [...new Set(userIds.filter(Boolean))];
  if (!uniqueIds.length) return map;

  const relaciones: { usuario_id: string; ciudad_id: number }[] = [];
  for (let i = 0; i < uniqueIds.length; i += USUARIOS_CIUDADES_IN_CHUNK) {
    const chunk = uniqueIds.slice(i, i + USUARIOS_CIUDADES_IN_CHUNK);
    const { data, error } = await supabase
      .from('usuarios_ciudades')
      .select('usuario_id, ciudad_id')
      .in('usuario_id', chunk);
    if (error) {
      console.warn('Error al obtener ciudades de usuarios:', error);
      return map;
    }
    if (data?.length) relaciones.push(...(data as { usuario_id: string; ciudad_id: number }[]));
  }

  const ciudadIds = [...new Set(relaciones.map((r) => r.ciudad_id).filter((id) => id != null))];
  const nombresById = new Map<number, string>();
  for (let i = 0; i < ciudadIds.length; i += USUARIOS_CIUDADES_IN_CHUNK) {
    const chunk = ciudadIds.slice(i, i + USUARIOS_CIUDADES_IN_CHUNK);
    const { data, error } = await supabase.from('ciudades').select('id, nombre').in('id', chunk);
    if (error) {
      console.warn('Error al obtener nombres de ciudades:', error);
      break;
    }
    data?.forEach((c: { id: number; nombre: string }) => {
      nombresById.set(c.id, c.nombre);
    });
  }

  for (const rel of relaciones) {
    if (!map.has(rel.usuario_id)) {
      map.set(rel.usuario_id, { ids: [], nombres: [] });
    }
    const entry = map.get(rel.usuario_id)!;
    const idStr = String(rel.ciudad_id);
    if (entry.ids.includes(idStr)) continue;
    entry.ids.push(idStr);
    const nombre = nombresById.get(rel.ciudad_id);
    if (nombre) entry.nombres.push(nombre);
  }
  return map;
}

/** Avatar de OAuth (Google suele usar `avatar_url` o `picture`). */
function avatarFromUserMetadata(
  meta: Record<string, unknown> | null | undefined
): string | null {
  if (!meta) return null;
  for (const key of ['avatar_url', 'picture'] as const) {
    const v = meta[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function nombreFromUserMetadata(
  meta: Record<string, unknown> | null | undefined
): string {
  if (!meta) return '';
  for (const key of ['nombre', 'full_name', 'name'] as const) {
    const v = meta[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/**
 * URL a la que Supabase redirige tras confirmar el correo (plantilla «Confirm signup»).
 * Debe figurar en Supabase Dashboard → Authentication → URL Configuration → Redirect URLs.
 */
export function getComensalSignupEmailRedirectTo(): string | undefined {
  if (typeof window === 'undefined' || !window.location?.origin) return undefined;
  // Web: página pública de confirmación. Nativo: flujo de registro en la app.
  if (Platform.OS === 'web') {
    return `${window.location.origin}/comensal/correo-verificado`;
  }
  return `${window.location.origin}/registro/confirmar`;
}

function readRpcBoolean(data: unknown, etiqueta: 'telefono' | 'email'): boolean {
  if (data === true || data === 't' || data === 'true') return true;
  if (data === false || data === 'f' || data === 'false') return false;
  console.warn(`[signUpComensal] RPC ${etiqueta}: valor inesperado`, data);
  throw new Error(
    etiqueta === 'telefono'
      ? 'No se pudo comprobar el teléfono. Intenta de nuevo.'
      : 'No se pudo comprobar el correo. Intenta de nuevo.'
  );
}

export const authService = {
  // ============================================
  // AUTENTICACIÓN
  // ============================================

  /**
   * Iniciar sesión con email y contraseña
   */
  async signIn(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // No bloquear el login si falla el registro de último acceso
      if (data.user) {
        const uid = data.user.id;
        setTimeout(() => {
          void this.updateLastAccess(uid);
        }, 2500);
      }

      return { user: data.user, session: data.session };
    } catch (error: any) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  },

  /**
   * Registro comensal: email y contraseña; teléfono opcional en metadata (vacío si no se pide en el formulario).
   */
  async signUpComensal(email: string, password: string) {
    const trimmedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      throw new Error('El email proporcionado no es válido');
    }
    if (!password || password.length < 6) {
      throw new Error('La contraseña debe tener al menos 6 caracteres');
    }

    const { data: emailRaw, error: emailRpcError } = await supabase.rpc('registro_email_en_uso', {
      p_email: trimmedEmail,
    });
    if (emailRpcError) {
      console.error('registro_email_en_uso:', emailRpcError);
      throw new Error('No se pudo comprobar el correo. Intenta de nuevo.');
    }
    if (readRpcBoolean(emailRaw, 'email')) {
      throw new Error(REGISTRO_EMAIL_EN_USO);
    }

    const emailRedirectTo = getComensalSignupEmailRedirectTo();

    const { data, error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password,
      options: {
        ...(emailRedirectTo ? { emailRedirectTo } : {}),
        data: {
          nombre: '',
          apellido: '',
          telefono: '',
        },
      },
    });

    if (error) throw error;

    const signedUser = data.user;
    if (signedUser && (!signedUser.identities || signedUser.identities.length === 0)) {
      throw new Error(REGISTRO_EMAIL_EN_USO);
    }

    /* Sesión nula si «Confirm email» está activo: el usuario abre el enlace del correo y vuelve con sesión. */
    return { user: data.user ?? null, session: data.session ?? null };
  },

  /** Reenvía el correo de confirmación de registro (misma URL de retorno que en signUp). */
  async resendSignupConfirmationEmail(email: string) {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) throw new Error('Indica tu correo electrónico');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) throw new Error('El correo no es válido');
    const redirectTo = getComensalSignupEmailRedirectTo();
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: trimmed,
      ...(redirectTo ? { options: { emailRedirectTo: redirectTo } } : {}),
    });
    if (error) throw error;
  },

  /**
   * Envía el código / magic link al correo para verificar el email en el registro.
   * Mismo flujo que «cambiar contraseña»: signInWithOtp + plantilla «Magic Link» en Supabase con {{ .Token }}.
   */
  async requestSignupEmailOtp(email: string) {
    await this.requestPasswordRecoveryOtp(email);
  },

  /** Valida el código recibido (tipo email / magic link OTP, igual que recuperación de contraseña sin cambiar clave). */
  async verifySignupEmailOtp(email: string, token: string): Promise<{ session: Session | null; user: User | null }> {
    const trimmed = email.trim().toLowerCase();
    const code = token.trim().replace(/\s/g, '');
    if (!code) throw new Error('Indica el código del correo');
    const { data, error } = await supabase.auth.verifyOtp({
      email: trimmed,
      token: code,
      type: 'email',
    });
    if (error) throw error;
    return {
      session: data.session ?? null,
      user: data.user ?? null,
    };
  },

  /**
   * Asocia el teléfono al usuario y envía SMS (proveedor SMS configurado en Supabase).
   */
  async requestPhoneVerificationOtp(phoneE164: string) {
    const phone = normalizePhoneE164(phoneE164);
    if (!phone || phone.length < 10) throw new Error('Teléfono no válido');
    const { error } = await supabase.auth.updateUser({ phone });
    if (error) throw error;
  },

  async verifyPhoneOtp(phoneE164: string, token: string): Promise<{ session: Session | null; user: User | null }> {
    const phone = normalizePhoneE164(phoneE164);
    const code = token.trim().replace(/\s/g, '');
    if (!code) throw new Error('Indica el código SMS');
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token: code,
      type: 'sms',
    });
    if (error) throw error;
    return {
      session: data.session ?? null,
      user: data.user ?? null,
    };
  },

  /** Paso final: perfil comensal (sexo, ciudad / «Otra», avatar opcional). */
  async completeComensalProfile(
    userId: string,
    payload: {
      nombre: string;
      apellido: string;
      fechaNacimiento: string;
      sexo: 'masculino' | 'femenino';
      ciudadId: number | null;
      /** Si `ciudadId` es null («Otra»), se guarda este texto (p. ej. "Otra"). */
      ciudadOtraLabel: string | null;
      avatarUrl?: string | null;
    }
  ) {
    const {
      nombre,
      apellido,
      fechaNacimiento,
      sexo,
      ciudadId,
      ciudadOtraLabel,
      avatarUrl,
    } = payload;

    const { error: metaError } = await supabase.auth.updateUser({
      data: { nombre: nombre.trim(), apellido: apellido.trim() },
    });
    if (metaError) throw metaError;

    await this.updateUserProfile(userId, {
      nombre: nombre.trim(),
      apellido: apellido.trim(),
      fecha_nacimiento: fechaNacimiento.trim() || undefined,
      sexo,
      ciudad_otra: ciudadId === null ? (ciudadOtraLabel?.trim() || 'Otra') : null,
      ...(avatarUrl ? { avatar_url: avatarUrl, foto_url: avatarUrl } : {}),
    });

    if (ciudadId !== null) {
      await setComensalCiudadPreferida(userId, ciudadId);
    } else {
      await clearComensalCiudades(userId);
    }
  },

  /**
   * Recuperación por código en la app (sin depender del “magic link”).
   * Usa el mismo endpoint que el magic link; si el correo solo dice “Follow this link…”,
   * en el dashboard hay que cambiar la plantilla, no este código:
   *
   * Supabase → Authentication → Email Templates → plantilla “Magic Link” (sign-in OTP).
   * Incluir en el HTML el token, por ejemplo:
   *   <p>Tu código: {{ .Token }}</p>
   * Sin {{ .Token }} en la plantilla, GoTrue solo envía el enlace.
   * Docs: https://supabase.com/docs/guides/auth/auth-email-passwordless#with-otp
   *
   * Solo usuarios ya registrados (shouldCreateUser: false).
   */
  async requestPasswordRecoveryOtp(email: string) {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) throw new Error('Indica tu correo electrónico');
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: { shouldCreateUser: false },
    });
    if (error) throw error;
  },

  /**
   * Valida el OTP recibido por correo y establece la nueva contraseña. Mantiene sesión iniciada.
   */
  async verifyOtpAndSetNewPassword(email: string, token: string, newPassword: string) {
    const trimmed = email.trim().toLowerCase();
    const code = token.trim().replace(/\s/g, '');
    if (!code) throw new Error('Indica el código');
    if (!newPassword || newPassword.length < 6) {
      throw new Error('La contraseña debe tener al menos 6 caracteres');
    }
    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: trimmed,
      token: code,
      type: 'email',
    });
    if (verifyError) throw verifyError;
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) throw updateError;
  },

  /**
   * Cerrar sesión.
   * Intenta revocar en servidor (global); si no hay sesión, el token falló o GoTrue responde error,
   * se hace local para vaciar el cliente y no bloquear la UI.
   */
  async signOut() {
    console.log('🔍 authService: signOut iniciado');
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        const { error } = await supabase.auth.signOut({ scope: 'global' });
        if (error) {
          console.warn('⚠️ authService: logout global no aplicado (se limpia local):', error.message);
        }
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn('⚠️ authService: signOut global omitido:', msg);
    }

    try {
      const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
      if (localError) {
        console.warn('⚠️ authService: signOut local:', localError.message);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn('⚠️ authService: signOut local excepción:', msg);
    }

    console.log('✅ authService: Sesión cerrada en este cliente');
  },

  /**
   * Obtener sesión actual
   */
  async getSession() {
    try {
      console.log('🔍 authService: getSession iniciado');
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('❌ authService: Error al obtener sesión', error);
        throw error;
      }
      console.log('🔍 authService: Sesión obtenida', session ? 'Sí' : 'No');
      return session;
    } catch (error: any) {
      console.error('❌ authService: Excepción al obtener sesión:', error);
      throw error;
    }
  },

  /**
   * Obtener usuario actual (desde la sesión persistida).
   * En React Native, getUser() puede lanzar AuthSessionMissingError aunque getSession() tenga datos;
   * para el estado de la app basta con session.user.
   */
  async getCurrentUser() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session?.user ?? null;
    } catch (error: any) {
      console.error('Error al obtener usuario:', error);
      throw error;
    }
  },

  /**
   * Perfil por id de usuario (sin `getSession`).
   * Evita interbloqueos si se llama desde `onAuthStateChange` de Supabase.
   */
  async getProfileByUserId(
    userId: string,
    opts?: {
      email?: string | null;
      userMetadata?: Record<string, unknown> | null;
      signal?: AbortSignal;
      accessToken?: string | null;
    }
  ): Promise<UserProfile | null> {
    try {
      if (opts?.accessToken) {
        const rows = await restGetJson(
          `user_profiles?id=eq.${encodeURIComponent(userId)}&select=*`,
          opts.accessToken,
          opts.signal
        );
        const data = Array.isArray(rows) ? rows[0] : rows;
        if (!data) return null;
        const ciudades = await fetchCiudadesWithToken(userId, opts.accessToken, opts.signal);
        return withNormalizedPhotoFields({
          ...data,
          ciudades_ids: ciudades.ids,
          ciudades_nombres: ciudades.nombres,
        }) as UserProfile;
      }

      let profileQuery = supabase.from('user_profiles').select('*').eq('id', userId);
      if (opts?.signal) profileQuery = profileQuery.abortSignal(opts.signal);
      const { data, error } = await profileQuery.single();

      if (error && error.code === 'PGRST116') {
        const meta = opts?.userMetadata ?? {};
        const metaAvatar = avatarFromUserMetadata(meta);
        const { data: newProfile, error: createError } = await supabase
          .from('user_profiles')
          .insert({
            id: userId,
            email: opts?.email || '',
            nombre: nombreFromUserMetadata(meta) || (typeof meta.nombre === 'string' ? meta.nombre : ''),
            role: typeof meta.role === 'string' ? meta.role : 'user',
            status: 'active',
            ...(metaAvatar ? { avatar_url: metaAvatar, foto_url: metaAvatar } : {}),
          })
          .select()
          .single();

        if (createError) {
          console.error('❌ authService: Error al crear perfil:', createError);
          return null;
        }
        const ciudades = await this.getUserCiudades(userId);
        return withNormalizedPhotoFields({
          ...newProfile,
          ciudades_ids: ciudades.ids,
          ciudades_nombres: ciudades.nombres,
        }) as UserProfile;
      }

      if (error) {
        console.error('❌ authService: Error al obtener perfil:', error);
        return null;
      }

      const ciudades = await Promise.race([
        this.getUserCiudades(userId),
        new Promise<CiudadesUsuario>((resolve) =>
          setTimeout(() => resolve({ ids: [], nombres: [] }), 2500)
        ),
      ]);
      let profile = withNormalizedPhotoFields({
        ...data,
        ciudades_ids: ciudades.ids,
        ciudades_nombres: ciudades.nombres,
      }) as UserProfile;

      // Si el perfil no tiene foto pero Auth/Google sí, sincronizar a user_profiles.
      const metaAvatar = avatarFromUserMetadata(opts?.userMetadata);
      if (metaAvatar && !getUserProfilePhotoUrl(profile)) {
        profile = { ...profile, avatar_url: metaAvatar, foto_url: metaAvatar };
        void supabase
          .from('user_profiles')
          .update({ avatar_url: metaAvatar, foto_url: metaAvatar })
          .eq('id', userId)
          .then(({ error: syncError }) => {
            if (syncError) {
              console.warn('[getProfileByUserId] No se pudo sincronizar avatar de Google:', syncError.message);
            }
          });
      }

      return profile;
    } catch (error: unknown) {
      const aborted =
        (typeof error === 'object' &&
          error !== null &&
          'name' in error &&
          (error as { name?: string }).name === 'AbortError') ||
        (error instanceof Error && /abort/i.test(error.message));
      if (aborted) throw error;
      console.error('❌ authService: Excepción al obtener perfil por id:', error);
      return null;
    }
  },

  /**
   * Obtener perfil del usuario actual (usa getSession; no llamar desde onAuthStateChange).
   */
  async getCurrentProfile(): Promise<UserProfile | null> {
    try {
      const user = await this.getCurrentUser();
      if (!user) return null;
      return this.getProfileByUserId(user.id, {
        email: user.email,
        userMetadata: user.user_metadata as Record<string, unknown>,
      });
    } catch (error: unknown) {
      console.error('❌ authService: Excepción al obtener perfil:', error);
      return null;
    }
  },

  /**
   * Obtener ciudades de un usuario
   */
  async getUserCiudades(userId: string): Promise<{ ids: string[], nombres: string[] }> {
    try {
      const ciudadesMap = await fetchCiudadesMapForUsers([userId]);
      return ciudadesMap.get(userId) || { ids: [], nombres: [] };
    } catch (error: any) {
      console.error('Error al obtener ciudades del usuario:', error);
      return { ids: [], nombres: [] };
    }
  },

  /**
   * Actualizar último acceso
   */
  async updateLastAccess(userId: string) {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ ultimo_acceso: new Date().toISOString() })
        .eq('id', userId);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error al actualizar último acceso:', error);
      // No lanzar error, es solo un registro
    }
  },

  // ============================================
  // GESTIÓN DE USUARIOS (Owner/Master)
  // ============================================

  /**
   * Crear nuevo usuario
   * Usa Edge Function para crear usuarios sin requerir confirmación de email.
   * Requiere que la Edge Function 'create-user' esté desplegada en Supabase.
   */
  async createUser(
    email: string,
    password: string,
    profile: {
      nombre?: string;
      apellido?: string;
      telefono?: string;
      cedula?: string;
      cargo?: string;
      instagram?: string;
      fecha_nacimiento?: string;
      talla?: string;
      role: UserRole;
      ciudades_ids?: string[];
    }
  ) {
    try {
      // Validar email antes de intentar crear el usuario
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const trimmedEmail = email.trim().toLowerCase();
      
      if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
        throw new Error('El email proporcionado no es válido');
      }

      // Validar que la contraseña tenga al menos 6 caracteres
      if (!password || password.length < 6) {
        throw new Error('La contraseña debe tener al menos 6 caracteres');
      }

      // Verificar sesión activa
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      console.log('🔑 Llamando Edge Function create-user...');
      
      // Usar supabase.functions.invoke para crear usuario sin confirmación de email
      const { data, error: invokeError } = await supabase.functions.invoke('create-user', {
        body: {
          email: trimmedEmail,
          password,
          profile: {
            nombre: profile.nombre,
            apellido: profile.apellido,
            telefono: profile.telefono,
            cedula: profile.cedula,
            cargo: profile.cargo,
            instagram: profile.instagram,
            fecha_nacimiento: profile.fecha_nacimiento,
            talla: profile.talla,
            role: profile.role,
            ciudades_ids: profile.ciudades_ids || [],
          },
        },
      });
      
      if (invokeError) {
        console.error('❌ Error al invocar Edge Function:', invokeError);
        
        // Si es un error de conexión, la función probablemente no está desplegada
        if (invokeError.message?.includes('Failed to fetch') || 
            invokeError.message?.includes('Failed to send a request') ||
            invokeError.message?.includes('NetworkError') || 
            invokeError.message?.includes('fetch')) {
          throw new Error(
            '❌ La Edge Function "create-user" no está desplegada.\n\n' +
            'Por favor, despliégala siguiendo estos pasos:\n' +
            '1. Ve a https://supabase.com/dashboard\n' +
            '2. Selecciona tu proyecto\n' +
            '3. Ve a "Edge Functions" en el menú lateral\n' +
            '4. Haz clic en "Create a new function"\n' +
            '5. Nombre: create-user\n' +
            '6. Copia el código de: supabase/functions/create-user/index.ts\n' +
            '7. Haz clic en "Deploy"\n\n' +
            'Consulta el archivo CREAR_USUARIO_EDGE_FUNCTION.md para más detalles.'
          );
        }
        
        throw invokeError;
      }
      
      // Verificar si hay un error en la respuesta de la función
      if (data?.error) {
        throw new Error(data.error);
      }
      
      if (!data?.user) {
        throw new Error('No se pudo crear el usuario');
      }

      console.log('✅ Usuario creado correctamente:', data.message);
      return data.user;
    } catch (error: any) {
      console.error('Error al crear usuario:', error);
      throw error;
    }
  },

  /**
   * Usuarios del team AS (owner/master/admin). No incluye comensales.
   */
  async getTeamUsers(): Promise<UserProfile[]> {
    try {
      const { data: usersData, error: usersError } = await supabase
        .from('user_profiles')
        .select('*')
        .in('role', ['owner', 'master', 'admin'])
        .order('fecha_creacion', { ascending: false });

      if (usersError) throw usersError;
      if (!usersData?.length) return [];

      const userIds = usersData.map((u) => u.id);
      const ciudadesMap = await fetchCiudadesMapForUsers(userIds);

      return usersData.map((user: any) => {
        const ciudades = ciudadesMap.get(user.id) || { ids: [], nombres: [] };
        return withNormalizedPhotoFields({
          ...user,
          ciudades_ids: ciudades.ids,
          ciudades_nombres: ciudades.nombres,
        }) as UserProfile;
      });
    } catch (error: any) {
      console.error('Error al obtener team:', error);
      throw error;
    }
  },

  /**
   * Solo el total de comensales registrados (role=user). Ligero para el badge al entrar.
   */
  async getComensalesCount(): Promise<number> {
    const { data, error } = await supabase.rpc('dashboard_count_comensales');
    if (!error && data != null) {
      const n = Number(Array.isArray(data) ? data[0] : data);
      if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
    }
    if (error) {
      console.warn('[getComensalesCount] RPC:', error.message);
    }

    const { count, error: countError } = await supabase
      .from('user_profiles')
      .select('id', { count: 'exact', head: true })
      .eq('role', 'user');
    if (countError) {
      console.error('[getComensalesCount] query error:', countError);
      throw countError;
    }
    return typeof count === 'number' ? count : 0;
  },

  /**
   * Comensales paginados (últimos primero). `search` filtra por nombre, apellido, email, teléfono o estado.
   */
  async getComensalesPage(options?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<{ users: UserProfile[]; total: number; page: number; pageSize: number }> {
    const pageSize = Math.max(1, Math.min(50, Math.floor(options?.pageSize ?? 10)));
    const page = Math.max(1, Math.floor(options?.page ?? 1));
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const rawSearch = (options?.search ?? '').trim();

    const mapRows = async (rows: Array<Record<string, unknown>>): Promise<UserProfile[]> => {
      const userIds = rows.map((u) => String(u.id ?? '')).filter(Boolean);
      const ciudadesMap = await fetchCiudadesMapForUsers(userIds);
      return rows.map((user) => {
        const ciudades = ciudadesMap.get(String(user.id)) || { ids: [], nombres: [] };
        return withNormalizedPhotoFields({
          ...user,
          role: (user.role as UserRole) || 'user',
          status: (user.status as UserStatus) || 'active',
          ciudades_ids: ciudades.ids,
          ciudades_nombres: ciudades.nombres,
        }) as UserProfile;
      });
    };

    try {
      const rpc = await supabase.rpc('dashboard_list_comensales', {
        p_search: rawSearch,
        p_page: page,
        p_page_size: pageSize,
      });
      if (!rpc.error) {
        const rows = (Array.isArray(rpc.data) ? rpc.data : []) as Array<
          Record<string, unknown> & { total_count?: number }
        >;
        const rawTotal = rows[0]?.total_count;
        const parsedTotal = Number(rawTotal);
        const total = Number.isFinite(parsedTotal)
          ? Math.max(0, Math.floor(parsedTotal))
          : rows.length;
        return {
          users: await mapRows(rows),
          total,
          page,
          pageSize,
        };
      }
      console.warn('[getComensalesPage] RPC:', rpc.error.message);

      let query = supabase
        .from('user_profiles')
        .select('*', { count: 'exact' })
        .eq('role', 'user')
        .order('fecha_creacion', { ascending: false });

      if (rawSearch) {
        // PostgREST: `*` = comodín (equivale a %). Evitar `%` literal y comillas, que suelen romper el `.or()`.
        // Espacios → `*` para "Juan Perez" ⇒ *Juan*Perez*
        const q = rawSearch
          .replace(/[%*,()]/g, ' ')
          .replace(/"/g, '')
          .trim()
          .replace(/\s+/g, '*');
        if (q) {
          const pattern = `*${q}*`;
          query = query.or(
            [
              `nombre.ilike.${pattern}`,
              `apellido.ilike.${pattern}`,
              `email.ilike.${pattern}`,
              `telefono.ilike.${pattern}`,
              `cedula.ilike.${pattern}`,
              `estado.ilike.${pattern}`,
            ].join(',')
          );
        }
      }

      let { data: usersData, error: usersError, count } = await query.range(from, to);

      // Si el filtro falla (p. ej. columna estado ausente), reintentar sin ella.
      if (usersError && rawSearch) {
        console.warn('[getComensalesPage] reintento sin columna estado:', usersError.message);
        const q = rawSearch
          .replace(/[%*,()]/g, ' ')
          .replace(/"/g, '')
          .trim()
          .replace(/\s+/g, '*');
        const pattern = `*${q}*`;
        const retry = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact' })
          .eq('role', 'user')
          .or(
            [
              `nombre.ilike.${pattern}`,
              `apellido.ilike.${pattern}`,
              `email.ilike.${pattern}`,
              `telefono.ilike.${pattern}`,
              `cedula.ilike.${pattern}`,
            ].join(',')
          )
          .order('fecha_creacion', { ascending: false })
          .range(from, to);
        usersData = retry.data;
        usersError = retry.error;
        count = retry.count;
      }

      if (usersError) {
        console.error('[getComensalesPage] query error:', usersError);
        throw usersError;
      }
      const total = typeof count === 'number' ? count : usersData?.length ?? 0;
      if (!usersData?.length) {
        return { users: [], total, page, pageSize };
      }

      const userIds = usersData.map((u) => u.id);
      const ciudadesMap = await fetchCiudadesMapForUsers(userIds);

      const users = usersData.map((user: any) => {
        const ciudades = ciudadesMap.get(user.id) || { ids: [], nombres: [] };
        return withNormalizedPhotoFields({
          ...user,
          ciudades_ids: ciudades.ids,
          ciudades_nombres: ciudades.nombres,
        }) as UserProfile;
      });

      return { users, total, page, pageSize };
    } catch (error: any) {
      console.error('Error al obtener comensales:', error);
      throw error;
    }
  },

  /**
   * Obtener todos los usuarios (solo Owner/Master)
   * @deprecated Preferir getTeamUsers / getComensalesPage para no cargar todo el listado.
   */
  async getAllUsers(): Promise<UserProfile[]> {
    try {
      // Obtener usuarios
      const { data: usersData, error: usersError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('fecha_creacion', { ascending: false });

      if (usersError) throw usersError;
      if (!usersData) return [];

      const userIds = usersData.map(u => u.id);
      const ciudadesMap = await fetchCiudadesMapForUsers(userIds);

      return usersData.map((user: any) => {
        const ciudades = ciudadesMap.get(user.id) || { ids: [], nombres: [] };
        return withNormalizedPhotoFields({
          ...user,
          ciudades_ids: ciudades.ids,
          ciudades_nombres: ciudades.nombres,
        }) as UserProfile;
      });
    } catch (error: any) {
      console.error('Error al obtener usuarios:', error);
      throw error;
    }
  },

  /**
   * Obtener usuario por ID
   */
  async getUserById(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return withNormalizedPhotoFields(data as Record<string, unknown>) as UserProfile;
    } catch (error: any) {
      console.error('Error al obtener usuario:', error);
      return null;
    }
  },

  /**
   * Actualizar perfil de usuario
   */
  async updateUserProfile(
    userId: string,
    updates: Partial<Omit<UserProfile, 'id' | 'fecha_creacion' | 'fecha_actualizacion' | 'ciudades_ids' | 'ciudades_nombres'>>
  ) {
    try {
      // Convertir strings vacíos a undefined para campos opcionales (especialmente fecha_nacimiento)
      const cleanedUpdates: any = { ...updates };
      if (cleanedUpdates.fecha_nacimiento === '' || cleanedUpdates.fecha_nacimiento === null) {
        cleanedUpdates.fecha_nacimiento = undefined;
      }
      if (cleanedUpdates.instagram === '') {
        cleanedUpdates.instagram = undefined;
      }
      if (cleanedUpdates.cedula === '') {
        cleanedUpdates.cedula = undefined;
      }
      if (cleanedUpdates.talla === '') {
        cleanedUpdates.talla = undefined;
      }

      // Mantener foto_url y avatar_url alineados (la app lee ambos; producción usa foto_url).
      const photoUpdate =
        cleanedUpdates.foto_url !== undefined
          ? cleanedUpdates.foto_url
          : cleanedUpdates.avatar_url !== undefined
            ? cleanedUpdates.avatar_url
            : undefined;
      if (photoUpdate !== undefined) {
        const normalized =
          photoUpdate == null || String(photoUpdate).trim() === ''
            ? null
            : String(photoUpdate).trim();
        cleanedUpdates.foto_url = normalized;
        cleanedUpdates.avatar_url = normalized;
      }
      
      const { data, error } = await supabase
        .from('user_profiles')
        .update(cleanedUpdates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return withNormalizedPhotoFields(data as Record<string, unknown>);
    } catch (error: any) {
      console.error('Error al actualizar perfil:', error);
      throw error;
    }
  },

  /**
   * Actualizar ciudades de un usuario
   * También actualiza el admin_id de las ciudades si el usuario es admin
   */
  async updateUserCiudades(userId: string, ciudades_ids: string[]) {
    try {
      // Obtener el rol del usuario para verificar si es admin
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.warn('Error obteniendo perfil del usuario:', profileError);
      }

      const isAdmin = userProfile?.role === 'admin';

      // Eliminar relaciones existentes
      const { error: deleteError } = await supabase
        .from('usuarios_ciudades')
        .delete()
        .eq('usuario_id', userId);

      if (deleteError && !deleteError.message.includes('does not exist')) {
        console.warn('Error eliminando ciudades del usuario:', deleteError);
      }

      // Insertar nuevas relaciones si hay ciudades
      if (ciudades_ids && ciudades_ids.length > 0) {
        const relaciones = ciudades_ids.map(ciudad_id => ({
          usuario_id: userId,
          ciudad_id: parseInt(ciudad_id)
        }));

        const { error: insertError } = await supabase
          .from('usuarios_ciudades')
          .insert(relaciones);

        if (insertError && !insertError.message.includes('does not exist')) {
          console.warn('Error insertando ciudades del usuario:', insertError);
        }

        // Si el usuario es admin, actualizar el admin_id de las ciudades asignadas
        if (isAdmin) {
          const ciudadIdsInt = ciudades_ids.map(id => parseInt(id));
          const { error: updateCiudadesError } = await supabase
            .from('ciudades')
            .update({ admin_id: userId })
            .in('id', ciudadIdsInt);

          if (updateCiudadesError) {
            console.warn('Error actualizando admin_id de ciudades:', updateCiudadesError);
          }

          // También limpiar admin_id de ciudades que ya no están asignadas a este admin
          const { data: ciudadesActuales, error: ciudadesError } = await supabase
            .from('ciudades')
            .select('id')
            .eq('admin_id', userId);

          if (!ciudadesError && ciudadesActuales) {
            const ciudadesAsignadasIds = new Set(ciudadIdsInt);
            const ciudadesParaLimpiar = ciudadesActuales
              .map(c => c.id)
              .filter(id => !ciudadesAsignadasIds.has(id));

            if (ciudadesParaLimpiar.length > 0) {
              const { error: limpiarError } = await supabase
                .from('ciudades')
                .update({ admin_id: null })
                .in('id', ciudadesParaLimpiar);

              if (limpiarError) {
                console.warn('Error limpiando admin_id de ciudades no asignadas:', limpiarError);
              }
            }
          }
        }
      } else {
        // Si no hay ciudades asignadas y el usuario es admin, limpiar admin_id de todas sus ciudades
        if (isAdmin) {
          const { error: limpiarError } = await supabase
            .from('ciudades')
            .update({ admin_id: null })
            .eq('admin_id', userId);

          if (limpiarError) {
            console.warn('Error limpiando admin_id de ciudades:', limpiarError);
          }
        }
      }
    } catch (error: any) {
      // Si la tabla no existe, solo loggear y continuar
      if (error.message && error.message.includes('does not exist')) {
        console.warn('La tabla usuarios_ciudades no existe aún. Las ciudades no se guardaron.');
      } else {
        console.error('Error actualizando ciudades del usuario:', error);
      }
    }
  },

  /**
   * Eliminar usuario vía Edge Function `delete-user` (permisos, user_profiles, auth.users).
   * - Si userId es el de la sesión actual: autoborrado (p. ej. comensal en /perfil).
   * - Si es otro id: requiere rol Owner o Master en la sesión actual.
   */
  async deleteUser(userId: string) {
    try {
      const normalizedId = userId.trim().toLowerCase();
      console.log('🗑️ deleteUser: Intentando eliminar usuario', normalizedId);

      // Verificar sesión activa
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      console.log('🔑 Llamando Edge Function delete-user...');
      
      // Usar supabase.functions.invoke es la forma recomendada de llamar Edge Functions
      const { data, error: invokeError } = await supabase.functions.invoke('delete-user', {
        body: { userId: normalizedId },
      });

      if (invokeError) {
        console.error('❌ Error al invocar Edge Function:', invokeError);

        if (
          invokeError.message?.includes('Failed to fetch') ||
          invokeError.message?.includes('NetworkError') ||
          invokeError.message?.includes('fetch')
        ) {
          throw new Error(
            'La Edge Function "delete-user" no está disponible. Despliégala en Supabase → Edge Functions.'
          );
        }

        let serverMessage: string | undefined;
        if (invokeError instanceof FunctionsHttpError && invokeError.context) {
          try {
            const body = await invokeError.context.clone().json();
            if (typeof body?.error === 'string' && body.error.length > 0) {
              serverMessage = body.error;
            }
          } catch {
            /* cuerpo no JSON */
          }
        }
        throw new Error(serverMessage ?? invokeError.message ?? 'Error al eliminar la cuenta');
      }
      
      // Verificar si hay un error en la respuesta de la función
      if (data?.error) {
        throw new Error(data.error);
      }
      
      // Si llegamos aquí, la función se ejecutó correctamente
      console.log('✅ Usuario eliminado completamente:', data?.message || 'Usuario eliminado correctamente');
    } catch (error: any) {
      console.error('❌ Error al eliminar usuario:', error);
      throw error;
    }
  },

  /**
   * Actualizar email y/o contraseña de un usuario (Owner/Master).
   * Usa la Edge Function `update-user-credentials` (Admin API, sin verificación por correo).
   */
  async updateUserCredentials(
    userId: string,
    opts: { email?: string; password?: string }
  ) {
    const trimmedId = userId.trim().toLowerCase();
    const email = opts.email?.trim().toLowerCase();
    const password = opts.password?.trim() ?? '';

    if (!email && password.length === 0) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const body: { userId: string; email?: string; password?: string } = {
        userId: trimmedId,
      };
      if (email) body.email = email;
      if (password.length > 0) body.password = password;

      const { data, error: invokeError } = await supabase.functions.invoke('update-user-credentials', {
        body,
      });

      if (invokeError) {
        console.error('Error al invocar update-user-credentials:', invokeError);

        if (
          invokeError.message?.includes('Failed to fetch') ||
          invokeError.message?.includes('Failed to send a request') ||
          invokeError.message?.includes('NetworkError') ||
          invokeError.message?.includes('fetch')
        ) {
          throw new Error(
            'La Edge Function "update-user-credentials" no está disponible. Despliégala en Supabase → Edge Functions (código en supabase/functions/update-user-credentials).'
          );
        }

        let serverMessage: string | undefined;
        if (invokeError instanceof FunctionsHttpError && invokeError.context) {
          try {
            const respBody = await invokeError.context.clone().json();
            if (typeof respBody?.error === 'string' && respBody.error.length > 0) {
              serverMessage = respBody.error;
            }
          } catch {
            /* cuerpo no JSON */
          }
        }
        throw new Error(serverMessage ?? invokeError.message ?? 'Error al actualizar credenciales');
      }

      if (data?.error) {
        throw new Error(data.error);
      }
    } catch (error: any) {
      console.error('Error al actualizar credenciales:', error);
      throw error;
    }
  },

  // ============================================
  // PERMISOS
  // ============================================

  /**
   * Obtener todos los permisos disponibles
   */
  async getAllPermisos(): Promise<Permiso[]> {
    try {
      const { data, error } = await supabase
        .from('permisos')
        .select('*')
        .order('categoria', { ascending: true })
        .order('nivel', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error al obtener permisos:', error);
      throw error;
    }
  },

  /**
   * Obtener permisos de un usuario
   */
  async getUserPermisos(userId: string): Promise<UsuarioPermiso[]> {
    try {
      const { data, error } = await supabase
        .from('usuario_permisos')
        .select(`
          *,
          permiso:permisos(*)
        `)
        .eq('usuario_id', userId);

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error al obtener permisos del usuario:', error);
      throw error;
    }
  },

  /**
   * Otorgar permiso a usuario
   */
  async grantPermission(userId: string, permisoId: number) {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) throw new Error('Usuario no autenticado');

      const { data, error } = await supabase
        .from('usuario_permisos')
        .insert({
          usuario_id: userId,
          permiso_id: permisoId,
          otorgado_por: currentUser.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error al otorgar permiso:', error);
      throw error;
    }
  },

  /**
   * Revocar permiso de usuario
   */
  async revokePermission(userId: string, permisoId: number) {
    try {
      const { error } = await supabase
        .from('usuario_permisos')
        .delete()
        .eq('usuario_id', userId)
        .eq('permiso_id', permisoId);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error al revocar permiso:', error);
      throw error;
    }
  },

  /**
   * Verificar si usuario tiene permiso
   */
  async hasPermission(userId: string, permissionCode: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('has_permission', {
        user_id: userId,
        permission_code: permissionCode,
      });

      if (error) throw error;
      return data || false;
    } catch (error: any) {
      console.error('Error al verificar permiso:', error);
      return false;
    }
  },

  /**
   * Verificar si el usuario actual tiene permiso
   */
  async currentUserHasPermission(permissionCode: string): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      if (!user) return false;

      const profile = await this.getCurrentProfile();
      if (!profile) return false;

      // Owner y Master tienen todos los permisos
      if (profile.role === 'owner' || profile.role === 'master') {
        return true;
      }

      // Para admins, verificar permiso específico
      if (profile.role === 'admin') {
        return await this.hasPermission(user.id, permissionCode);
      }

      return false;
    } catch (error: any) {
      console.error('Error al verificar permiso del usuario actual:', error);
      return false;
    }
  },
};

